import { determineApiBase } from './api';

function safeDetermineApiBase() {
  /* c8 ignore start */
  try {
    return typeof determineApiBase === 'function' ? determineApiBase() : '';
  } catch (e) {
    return '';
  }
  /* c8 ignore stop */
}

// Admin API helpers for site settings

function buildUrl(path) {
  const base = safeDetermineApiBase();
  // If no base configured, keep the relative path (tests assert this behavior)
  if (!base) return path;
  try {
    return new URL(path, base).toString();
  /* c8 ignore next */
  } catch (e) {
    /* c8 ignore next */
    return `${base.replace(/\/$/, '')}${path}`;
  }
}

async function fetchWithFallback(path, options) {
  // Try first with the buildUrl result (usually relative path)
  try {
    return await fetch(buildUrl(path), options);
  } catch (e) {
    // If Node's fetch rejects due to invalid relative URL, retry with a safe absolute base.
    if (e && (e.code === 'ERR_INVALID_URL' || /Invalid URL/i.test(String(e.message)))) {
      // determineApiBase may return a relative path like '/api' in some environments;
      // ensure we use an absolute base when constructing a fallback URL.
      const rawBase = safeDetermineApiBase();
      /* c8 ignore start */
      const safeBase = rawBase && /^https?:\/\//i.test(rawBase)
        ? rawBase
        : `http://localhost${rawBase || ''}`;
      /* c8 ignore stop */
      const url = new URL(path, safeBase).toString();
      return fetch(url, options);
    }
    throw e;
  }
}
export async function fetchSiteSettings() {
  const res = await fetchWithFallback('/api/admin/site-settings/footer', { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function updateSiteFooter(text, termsOfService, privacyPolicy, contact) {
  const res = await fetchWithFallback('/api/admin/site-settings/footer', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, termsOfService, privacyPolicy, contact }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function fetchSiteTitle() {
  const res = await fetchWithFallback('/api/admin/site-settings/title', { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchSiteFreeTier() {
  const res = await fetchWithFallback('/api/admin/site-settings/free-tier', { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function updateSiteTitle(title) {
  const res = await fetchWithFallback('/api/admin/site-settings/title', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function fetchAdminUsersCount() {
  const res = await fetchWithFallback('/api/admin/users?page=1&limit=1', { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  const data = await res.json();
  // Expecting { users: [...], total: number }
  return { total: data.total ?? null };
}

// Waiting-list / settings endpoints
export async function fetchAdminSettings() {
  const res = await fetchWithFallback('/api/admin/settings', { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function updateAdminSettings(payload) {
  const res = await fetchWithFallback('/api/admin/settings', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchContactSettings() {
  const res = await fetchWithFallback('/api/settings/contact', { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function updateContactSettings(payload) {
  const res = await fetchWithFallback('/api/admin/settings', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}


export async function fetchWaitingListStats() {
  const res = await fetchWithFallback('/api/admin/waiting-list/stats', { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchTiers() {
  const res = await fetchWithFallback('/api/tiers', { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchBadges() {
  const res = await fetchWithFallback('/api/admin/badges', { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function updateBadgeDefinition(id, payload) {
  const res = await fetchWithFallback(`/api/admin/badges/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

// Alias for callers that use the legacy name from the removed admin.js service.
export { fetchAdminSettings as getAdminSettings };

export async function sendNewsletter(payload) {
  const res = await fetchWithFallback('/api/admin/newsletter', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function previewNewsletter(payload) {
  const res = await fetchWithFallback('/api/admin/newsletter/preview', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchActiveUsers() {
  const res = await fetchWithFallback('/api/admin/users?status=active&limit=100', {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return (data.users || []).map(u => u.twitch_username).filter(Boolean);
}

// Denied tags API endpoints
export async function fetchDeniedTags(limit = 1000, offset = 0) {
  const res = await fetchWithFallback(`/api/admin/denied-tags?limit=${limit}&offset=${offset}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function addDeniedTag(tag, reason = null) {
  const res = await fetchWithFallback('/api/admin/denied-tags', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag, reason }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function removeDeniedTag(id) {
  const res = await fetchWithFallback(`/api/admin/denied-tags/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return true;
}

/**
 * Fetch tag usage report with pagination and sorting
 * @param {number} limit - Max tags to return (default: 50)
 * @param {number} offset - Pagination offset (default: 0)
 * @param {string} sort - Sort by 'clip_count' or 'user_count' (default: 'clip_count')
 */
export async function fetchTagUsageReport(limit = 50, offset = 0, sort = 'clip_count') {
  const url = buildUrl('/api/admin/reports/tags', { limit, offset, sort });
  const res = await fetchWithFallback(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }

  return await res.json();
}

/**
 * Fetch dashboard statistics for admin overview
 * Returns: total_clips, active_creators_this_month, active_clippers_this_month,
 *          total_public_profiles, top_tags
 */
export async function fetchDashboardStats() {
  const res = await fetchWithFallback('/api/admin/reports/stats', {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }

  return await res.json();
}

export async function fetchBadgeUsers(badgeId = null) {
  const url = badgeId ? `/api/admin/reports/badge-users?badge_id=${badgeId}` : '/api/admin/reports/badge-users';
  const res = await fetchWithFallback(url, { method: 'GET', credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  return await res.json();
}
